import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, Clock, MapPin, Users, PawPrint, AlertTriangle, Megaphone, User,
  CheckCircle2, LogOut, Camera, Dog as DogIcon, Cat, Pin, Home, ArrowRight,
  Plus, X, Smartphone, Download, RefreshCw,
} from "lucide-react";

const EMPTY_ILLO = "https://static.prod-images.emergentagent.com/jobs/a42dfaab-b82a-4662-9387-494cabf3a7e3/images/926cde7f34b8d815b4fe25927508fec5319581edd830ab8a0b1a01cb27717523.png";

function issueStatusClass(status) {
  if (status === "resolved") return "bg-fern";
  if (status === "in_progress") return "bg-terracotta";
  return "bg-muted-foreground";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function VolunteerApp() {
  const { user, logout, refreshUser } = useAuth();
  const [tab, setTab] = useState("home");
  const [shifts, setShifts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [cats, setCats] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({ total_hours: 0, completed_shifts: 0, active_shifts: 0, max_active: 10, unique_dogs_walked: 0, unique_cats_handled: 0 });

  const loadAll = useCallback(async () => {
    const endpoints = [
      { key: "shifts", url: "/shifts?upcoming=true", setter: setShifts, fallback: [] },
      { key: "my_shifts", url: "/my/shifts", setter: setMyShifts, fallback: [] },
      { key: "dogs", url: "/dogs?active_only=true&species=dog", setter: setDogs, fallback: [] },
      { key: "cats", url: "/dogs?active_only=true&species=cat", setter: setCats, fallback: [] },
      { key: "bulletins", url: "/bulletins", setter: setBulletins, fallback: [] },
      { key: "issues", url: "/issues?mine=true", setter: setIssues, fallback: [] },
      { key: "stats", url: "/my/stats", setter: setStats, fallback: null },
    ];
    const results = await Promise.allSettled(endpoints.map((e) => api.get(e.url)));
    const failures = [];
    results.forEach((res, idx) => {
      const ep = endpoints[idx];
      if (res.status === "fulfilled") {
        ep.setter(res.value.data);
      } else {
        failures.push({ key: ep.key, url: ep.url, status: res.reason?.response?.status, msg: res.reason?.message });
        if (ep.fallback !== null) ep.setter(ep.fallback);
      }
    });
    if (failures.length > 0) {
      console.error("VolunteerApp loadAll partial failure:", failures);
      // Only warn if EVERYTHING failed — otherwise render partial data quietly so a
      // single bad endpoint doesn't blank the volunteer's whole home screen.
      if (failures.length === endpoints.length) {
        toast.error("Couldn't reach the server. Pull down to refresh or tap Reload app on the Shifts tab.");
      } else {
        // Non-critical: log for support triage but don't scare the volunteer.
        const keys = failures.map((f) => f.key).join(", ");
        console.warn(`Some sections couldn't load (${keys}) — showing what worked.`);
      }
    }
    // Refresh auth profile so newly-granted Woof Pack approval / role changes surface immediately
    refreshUser && refreshUser();
  }, [refreshUser]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Also re-fetch profile when the tab regains focus, so approvals granted while
  // the volunteer had the app open in the background take effect on return.
  useEffect(() => {
    function onFocus() { refreshUser && refreshUser(); }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUser]);

  const tabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "shifts", label: "Shifts", icon: CalendarDays },
    { id: "mine", label: "My shifts", icon: CheckCircle2 },
    { id: "dogs", label: "Dogs", icon: DogIcon },
    { id: "cats", label: "Cats", icon: Cat },
    { id: "report", label: "Report", icon: AlertTriangle },
    { id: "me", label: "Profile", icon: User },
  ];

  return (
    <div className="min-h-screen bg-sand pb-24 md:pb-10">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-sand/85 border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <div>
              <p className="label-caps leading-none">Volunteer</p>
              <h1 className="font-heading text-lg font-semibold leading-tight">Satchel&apos;s Last Resort</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">Hi, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="volunteer-logout"
              className="rounded-full">
              <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <InstallBanner />
        {/* Desktop tabs */}
        <div className="hidden md:flex gap-2 mb-6">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`vol-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition
                ${tab === t.id ? "bg-fern text-white" : "bg-white border border-border text-ink hover:bg-accent"}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "home" && <HomeTab user={user} stats={stats} bulletins={bulletins} onBrowseShifts={() => setTab("shifts")} />}
        {tab === "shifts" && <ShiftsTab shifts={shifts} stats={stats} reload={loadAll} reloadStats={loadAll} user={user} />}
        {tab === "mine" && <MyShiftsTab myShifts={myShifts} dogs={dogs} cats={cats} reload={loadAll} />}
        {tab === "dogs" && <AnimalListTab animals={dogs} species="dog" />}
        {tab === "cats" && <AnimalListTab animals={cats} species="cat" />}
        {tab === "report" && <ReportTab dogs={dogs} cats={cats} reload={loadAll} issues={issues} />}
        {tab === "me" && <ProfileTab user={user} stats={stats} />}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border">
        <div className="grid grid-cols-7">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`vol-bottomtab-${t.id}`}
              className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${tab === t.id ? "text-fern" : "text-muted-foreground"}`}>
              <t.icon className="w-5 h-5" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="tactile-card p-4">
      <p className="label-caps">{label}</p>
      <p className="font-heading text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}

function ShiftsTab({ shifts, stats, reload, reloadStats, user }) {
  const [openCategory, setOpenCategory] = useState(null);
  async function signup(id) {
    try {
      await api.post(`/shifts/${id}/signup`);
      toast.success("You're signed up!");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign up");
    }
  }
  const canDoWoofPack = !!user?.woof_pack_approved;

  // Group upcoming shifts by title
  const grouped = useMemo(() => {
    const g = {};
    for (const s of shifts) {
      (g[s.title] = g[s.title] || []).push(s);
    }
    return Object.entries(g).map(([title, list]) => ({
      title,
      list: list.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
      woof_pack: list.some((s) => s.woof_pack),
      total: list.length,
      open: list.filter((s) => s.signup_count < s.capacity).length,
    }));
  }, [shifts]);

  const activeCategory = grouped.find((c) => c.title === openCategory);
  const categoryLocked = activeCategory?.woof_pack && !canDoWoofPack;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatChip label="Total hours" value={stats.total_hours} />
        <StatChip label="Completed" value={stats.completed_shifts} />
        <StatChip label="Active" value={`${stats.active_shifts} / ${stats.max_active}`} />
        <StatChip label="Dogs walked" value={stats.unique_dogs_walked} />
        <StatChip label="Cats cared for" value={stats.unique_cats_handled || 0} />
      </div>

      {!openCategory && (
        <div>
          <h2 className="font-heading text-2xl font-semibold mb-1">Volunteer opportunities</h2>
          <p className="text-sm text-muted-foreground mb-4">Pick a category to see the schedule and sign up.</p>
          {grouped.length === 0 ? (
            <div className="tactile-card border-0 p-8 text-center space-y-4" data-testid="empty-shifts">
              <p className="text-muted-foreground">No upcoming shifts posted yet.</p>
              <p className="text-xs text-muted-foreground">
                On iPhone home-screen app and can see shifts on the website? Tap Reload — the installed app can hold onto an older version of the page.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  try { window.location.reload(); } catch { window.location.href = "/app"; }
                }}
                data-testid="shifts-reload-app"
                className="rounded-full"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Reload app
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-grid">
              {grouped.map((c) => {
                const isWoofLocked = c.woof_pack && !canDoWoofPack;
                return (
                  <button
                    key={c.title}
                    type="button"
                    onClick={() => setOpenCategory(c.title)}
                    data-testid={`category-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="tactile-card border-0 p-6 text-left hover:shadow-md transition group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center ${c.woof_pack ? "bg-ink" : "bg-fern/10"}`}>
                        {c.woof_pack ? <PawPrint className="w-5 h-5 text-white" strokeWidth={1.5} /> : <CalendarDays className="w-5 h-5 text-fern" strokeWidth={1.5} />}
                      </div>
                      {c.woof_pack && <Badge className="bg-ink text-white rounded-full text-[10px]">WOOF PACK</Badge>}
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-1">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {c.open} of {c.total} shifts open · next 30 days
                    </p>
                    {isWoofLocked && (
                      <p className="text-xs text-terracotta mt-2">Approval required to sign up</p>
                    )}
                    <p className="text-xs text-fern mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
                      View schedule <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeCategory && (
        <ScheduleView category={activeCategory} categoryLocked={categoryLocked} canDoWoofPack={canDoWoofPack} maxActive={stats.max_active} signup={signup} onBack={() => setOpenCategory(null)} />
      )}

      {!openCategory && (
        <div className="pt-4">
          <LogHoursCard reloadStats={reloadStats} />
        </div>
      )}
    </div>
  );
}

function ScheduleView({ category, categoryLocked, canDoWoofPack, maxActive, signup, onBack }) {
  const [view, setView] = useState("list");

  // Build a per-day map for calendar view
  const dayMap = useMemo(() => {
    const m = {};
    for (const s of category.list) {
      const key = s.starts_at.slice(0, 10); // YYYY-MM-DD
      (m[key] = m[key] || []).push(s);
    }
    return m;
  }, [category.list]);

  const days = useMemo(() => {
    // Calendar covers today through the last-scheduled day (max ~30 ahead)
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    // Align to Sunday
    const startAligned = new Date(start);
    startAligned.setDate(start.getDate() - start.getDay());
    // Find last shift date
    const lastKey = category.list.length > 0 ? category.list[category.list.length - 1].starts_at.slice(0, 10) : null;
    const lastDate = lastKey ? new Date(lastKey + "T00:00:00") : start;
    // Extend to end of that week (Saturday)
    const endAligned = new Date(lastDate);
    endAligned.setDate(lastDate.getDate() + (6 - lastDate.getDay()));
    const out = [];
    const cur = new Date(startAligned);
    while (cur <= endAligned) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [category.list]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthLabel = days.length > 0 ? days[Math.floor(days.length / 2)].toLocaleString(undefined, { month: "long", year: "numeric" }) : "";

  function shortTime(iso) {
    const d = new Date(iso);
    const h = d.getHours();
    return h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`;
  }

  return (
    <div>
      <button type="button" onClick={onBack} data-testid="back-to-categories"
        className="text-sm text-fern hover:underline mb-2 inline-flex items-center gap-1">
        ← Back to all opportunities
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-2xl font-semibold">{category.title}</h2>
          {category.woof_pack && <Badge className="bg-ink text-white rounded-full text-[10px]">WOOF PACK</Badge>}
        </div>
        <div className="flex gap-1 bg-muted rounded-full p-1" data-testid="view-toggle">
          <button type="button" onClick={() => setView("list")} data-testid="view-list"
            className={`px-3 py-1 text-xs rounded-full transition ${view === "list" ? "bg-white text-ink shadow-sm" : "text-muted-foreground"}`}>
            List
          </button>
          <button type="button" onClick={() => setView("calendar")} data-testid="view-calendar"
            className={`px-3 py-1 text-xs rounded-full transition ${view === "calendar" ? "bg-white text-ink shadow-sm" : "text-muted-foreground"}`}>
            Calendar
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Each shift is 2 hours. {category.woof_pack ? "Approved volunteers only — no cap on active shifts." : `Up to ${maxActive} active shifts at a time.`}
      </p>
      {categoryLocked && (
        <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-4 mb-4 text-sm">
          <p className="font-medium text-terracotta">Woof Pack approval required</p>
          <p className="text-muted-foreground">These shifts are for staff-approved volunteers only. Please reach out to the shelter team about qualifying.</p>
        </div>
      )}

      {view === "list" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="shifts-list">
          {category.list.map((s) => {
            const isFull = s.signup_count >= s.capacity;
            return (
              <Card key={s.id} className="tactile-card border-0 p-5" data-testid={`shift-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4" strokeWidth={1.5} />
                    {fmtDate(s.starts_at)}
                  </p>
                  <Badge variant={isFull ? "destructive" : "secondary"} className="rounded-full">
                    {s.signup_count}/{s.capacity}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {s.duration_hours}h</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {s.location}</span>
                </div>
                <Button onClick={() => signup(s.id)} disabled={isFull || (s.woof_pack && !canDoWoofPack)} data-testid={`signup-btn-${s.id}`}
                  className={`w-full rounded-full text-white ${s.woof_pack ? "bg-ink hover:bg-ink/90" : "bg-fern hover:bg-fern-dark"}`}>
                  {isFull ? "Full" : s.woof_pack && !canDoWoofPack ? "Approval required" : "Sign up"}
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {view === "calendar" && (
        <div className="tactile-card p-4" data-testid="calendar-view">
          <p className="label-caps text-center mb-3">{monthLabel}</p>
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-[10px] font-medium text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const key = d.toISOString().slice(0, 10);
              const shifts = (dayMap[key] || []).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
              const isPast = d < today;
              const isToday = d.getTime() === today.getTime();
              return (
                <div key={key}
                  className={`min-h-[80px] rounded-lg p-1.5 border text-left ${isPast ? "bg-muted/40 border-transparent opacity-50" : "bg-white border-border"} ${isToday ? "ring-2 ring-fern" : ""}`}
                  data-testid={`day-${key}`}>
                  <p className={`text-[10px] font-medium ${isToday ? "text-fern" : "text-muted-foreground"}`}>
                    {d.getDate()}
                  </p>
                  <div className="mt-1 space-y-0.5">
                    {shifts.map((s) => {
                      const isFull = s.signup_count >= s.capacity;
                      const locked = s.woof_pack && !canDoWoofPack;
                      const label = `${shortTime(s.starts_at)} ${s.signup_count}/${s.capacity}`;
                      let cls = "bg-fern text-white hover:bg-fern-dark";
                      if (s.woof_pack) cls = locked ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-ink text-white hover:bg-ink/90";
                      if (isFull) cls = "bg-muted text-muted-foreground cursor-not-allowed";
                      return (
                        <button key={s.id} type="button" disabled={isFull || locked || isPast}
                          onClick={() => signup(s.id)} data-testid={`cal-slot-${s.id}`}
                          title={`${s.title} — ${fmtDate(s.starts_at)} · ${s.location} · ${s.signup_count}/${s.capacity} filled`}
                          className={`w-full text-[10px] px-1 py-0.5 rounded ${cls} transition`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Click a time slot to sign up. Green = open · <span className="text-ink">Ink</span> = Woof Pack · Gray = full.
          </p>
        </div>
      )}
    </div>
  );
}

function MyShiftsTab({ myShifts, dogs, cats, reload }) {
  const [checkoutShift, setCheckoutShift] = useState(null);
  const [selectedDogs, setSelectedDogs] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [notes, setNotes] = useState("");

  async function cancel(id) {
    try { await api.post(`/shifts/${id}/cancel`); toast.success("Cancelled"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel"); }
  }
  async function checkin(id) {
    try { await api.post(`/shifts/${id}/checkin`); toast.success("Checked in! Have a great shift 🐾"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't check in"); }
  }
  async function doCheckout() {
    try {
      await api.post(`/shifts/${checkoutShift}/checkout`, { dog_ids: selectedDogs, cat_ids: selectedCats, notes });
      toast.success("2 hours logged! Thank you ❤️");
      setCheckoutShift(null); setSelectedDogs([]); setSelectedCats([]); setNotes("");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't check out"); }
  }

  const active = myShifts.filter((s) => ["signed_up", "checked_in"].includes(s.status));
  const past = myShifts.filter((s) => s.status === "completed");

  function checkinWindow(shift) {
    if (!shift?.starts_at || !shift?.ends_at) return { canCheckin: true };
    const start = new Date(shift.starts_at);
    const end = new Date(shift.ends_at);
    const dayStart = new Date(start); dayStart.setHours(6, 0, 0, 0);
    const latest = new Date(end.getTime() + 60 * 60 * 1000);
    const now = new Date();
    if (now < dayStart) {
      const dayLabel = start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
      return { canCheckin: false, reason: `Check-in opens 6 AM on ${dayLabel}` };
    }
    if (now > latest) return { canCheckin: false, reason: "This shift has already ended" };
    return { canCheckin: true };
  }

  return (
    <div className="space-y-8 animate-fade-up">
      <section>
        <h2 className="font-heading text-2xl font-semibold mb-4">Active shifts</h2>
        {active.length === 0 ? <EmptyState text="No active shifts. Browse the Shifts tab to sign up." /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {active.map((s) => (
              <Card key={s.signup_id} className="tactile-card border-0 p-6" data-testid={`myshift-${s.signup_id}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-heading text-lg font-semibold">{s.shift?.title || "Shift"}</h3>
                  <Badge className={`rounded-full ${s.status === "checked_in" ? "bg-terracotta" : "bg-fern"} text-white`}>
                    {s.status === "checked_in" ? "Checked in" : "Signed up"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{fmtDate(s.shift?.starts_at)}</p>
                <div className="flex flex-wrap gap-2">
                  {s.status === "signed_up" && (() => {
                    const win = checkinWindow(s.shift);
                    return (
                      <>
                        <Button size="sm" onClick={() => checkin(s.shift_id)}
                          disabled={!win.canCheckin}
                          title={win.reason || ""}
                          data-testid={`checkin-${s.signup_id}`}
                          className="rounded-full bg-fern hover:bg-fern-dark text-white disabled:opacity-60">
                          Check in
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => cancel(s.shift_id)} data-testid={`cancel-${s.signup_id}`}
                          className="rounded-full">Cancel</Button>
                      </>
                    );
                  })()}
                  {s.status === "signed_up" && (() => {
                    const win = checkinWindow(s.shift);
                    return win.canCheckin ? null : (
                      <p className="text-xs text-muted-foreground w-full" data-testid={`checkin-hint-${s.signup_id}`}>
                        {win.reason}
                      </p>
                    );
                  })()}
                  {s.status === "checked_in" && (
                    <Button size="sm" onClick={() => { setCheckoutShift(s.shift_id); setSelectedDogs([]); setSelectedCats([]); setNotes(""); }}
                      data-testid={`checkout-${s.signup_id}`}
                      className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
                      Check out &amp; log animals
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-heading text-2xl font-semibold mb-4">History</h2>
        {past.length === 0 ? <EmptyState text="Completed shifts will appear here." compact /> : (
          <div className="tactile-card divide-y divide-border">
            {past.map((s) => {
              const dogList = s.dog_names || [];
              const catList = s.cat_names || [];
              const anyAnimals = dogList.length + catList.length;
              return (
                <div key={s.signup_id} className="p-4" data-testid={`history-${s.signup_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{s.shift?.title || "Shift"}</p>
                      <p className="text-sm text-muted-foreground">
                        {fmtDate(s.shift?.starts_at)}
                        {anyAnimals > 0 ? ` · ${anyAnimals} animal(s) cared for` : ""}
                      </p>
                      {(dogList.length > 0 || catList.length > 0) && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {dogList.map((n, idx) => (
                            <span key={`d-${idx}`} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-fern/10 text-fern-dark px-2 py-0.5"
                              data-testid={`history-dog-${s.signup_id}-${idx}`}>
                              <DogIcon className="w-3 h-3" strokeWidth={1.5} /> {n}
                            </span>
                          ))}
                          {catList.map((n, idx) => (
                            <span key={`c-${idx}`} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-terracotta/10 text-terracotta px-2 py-0.5"
                              data-testid={`history-cat-${s.signup_id}-${idx}`}>
                              <Cat className="w-3 h-3" strokeWidth={1.5} /> {n}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge className="bg-fern text-white rounded-full shrink-0">+{s.hours}h</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog open={!!checkoutShift} onOpenChange={(o) => !o && setCheckoutShift(null)}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Log your shift</DialogTitle>
            <DialogDescription>Select the animals you cared for. Your 2 hours will be recorded.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-72 overflow-y-auto">
            {dogs.length > 0 && (
              <div>
                <p className="label-caps mb-2 flex items-center gap-1.5"><DogIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Dogs walked</p>
                <div className="space-y-2">
                  {dogs.map((d) => (
                    <label key={d.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border hover:bg-accent/40 cursor-pointer">
                      <Checkbox
                        checked={selectedDogs.includes(d.id)}
                        onCheckedChange={(c) => setSelectedDogs(c ? [...selectedDogs, d.id] : selectedDogs.filter((x) => x !== d.id))}
                        data-testid={`dog-check-${d.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{d.breed} {d.age && `· ${d.age}`}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {cats.length > 0 && (
              <div>
                <p className="label-caps mb-2 flex items-center gap-1.5"><Cat className="w-3.5 h-3.5" strokeWidth={1.5} /> Cats handled</p>
                <div className="space-y-2">
                  {cats.map((c) => (
                    <label key={c.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border hover:bg-accent/40 cursor-pointer">
                      <Checkbox
                        checked={selectedCats.includes(c.id)}
                        onCheckedChange={(v) => setSelectedCats(v ? [...selectedCats, c.id] : selectedCats.filter((x) => x !== c.id))}
                        data-testid={`cat-check-${c.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.breed} {c.age && `· ${c.age}`}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {dogs.length === 0 && cats.length === 0 && <p className="text-sm text-muted-foreground">No animals in roster yet.</p>}
          </div>
          <Textarea placeholder="Any notes about your shift? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="checkout-notes" className="rounded-xl" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutShift(null)} className="rounded-full">Cancel</Button>
            <Button onClick={doCheckout} data-testid="checkout-submit" className="rounded-full bg-fern hover:bg-fern-dark text-white">
              Log hours
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AnimalListTab({ animals, species }) {
  const isCat = species === "cat";
  const title = isCat ? "Cats in our care" : "Dogs in our care";
  const Icon = isCat ? Cat : PawPrint;
  const [selected, setSelected] = useState(null);
  return (
    <div className="animate-fade-up">
      <h2 className="font-heading text-2xl font-semibold mb-4">{title}</h2>
      {animals.length === 0 ? <EmptyState text={isCat ? "No cats added yet." : "No dogs added yet."} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${species}s-list`}>
          {animals.map((d) => (
            <button
              type="button"
              key={d.id}
              onClick={() => setSelected(d)}
              data-testid={`animal-card-${d.id}`}
              className="text-left focus:outline-none focus:ring-2 focus:ring-fern focus:ring-offset-2 rounded-2xl"
            >
              <Card className="tactile-card border-0 p-5 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-full ${isCat ? "bg-terracotta/10" : "bg-fern/10"} flex items-center justify-center overflow-hidden`}>
                    {d.photo_url ? (
                      <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
                    ) : (
                      <Icon className={`w-5 h-5 ${isCat ? "text-terracotta" : "text-fern"}`} strokeWidth={1.5} />
                    )}
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-lg leading-none">{d.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {d.breed || (isCat ? "Mixed breed cat" : "Mixed breed dog")}{d.age && ` · ${d.age}`}
                    </p>
                  </div>
                </div>
                {d.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{d.description}</p>
                )}
              </Card>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="animal-detail-dialog" className="max-w-lg p-0 overflow-hidden">
          {selected && (
            <>
              <div className={`w-full aspect-square bg-sand ${isCat ? "bg-terracotta/5" : "bg-fern/5"} flex items-center justify-center`}>
                {selected.photo_url ? (
                  <img src={selected.photo_url} alt={selected.name}
                    className="w-full h-full object-cover" />
                ) : (
                  <Icon className={`w-24 h-24 ${isCat ? "text-terracotta/40" : "text-fern/40"}`} strokeWidth={1} />
                )}
              </div>
              <div className="p-6 space-y-3">
                <DialogHeader className="space-y-1">
                  <DialogTitle className="font-heading text-2xl">{selected.name}</DialogTitle>
                  <DialogDescription>
                    {selected.breed || (isCat ? "Mixed breed cat" : "Mixed breed dog")}{selected.age && ` · ${selected.age}`}
                  </DialogDescription>
                </DialogHeader>
                {selected.description && (
                  <p className="text-sm text-ink whitespace-pre-wrap">{selected.description}</p>
                )}
                {selected.shelterluv_status && (
                  <p className="text-xs text-muted-foreground">Status: {selected.shelterluv_status}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HomeTab({ user, stats, bulletins, onBrowseShifts }) {
  const pinned = bulletins.filter((b) => b.pinned);
  const recent = bulletins.filter((b) => !b.pinned);
  const firstName = user?.name?.split(" ")[0] || "friend";

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Greeting + CTA */}
      <div className="tactile-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-caps">Welcome back</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Hi, {firstName}.</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {stats.active_shifts > 0
              ? `You have ${stats.active_shifts} active shift${stats.active_shifts === 1 ? "" : "s"} on your plate.`
              : "Ready to help the pack today?"}
          </p>
        </div>
        <Button onClick={onBrowseShifts} data-testid="home-browse-shifts"
          className="rounded-full bg-fern hover:bg-fern-dark text-white h-12 px-6">
          Browse shifts <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
        </Button>
      </div>

      {/* Bulletin board — the focal point */}
      <section className="bulletin-paper rounded-2xl p-6 sm:p-8 border border-border" data-testid="home-bulletins">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl font-semibold">Bulletin board</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">News and announcements from the shelter team.</p>

        {bulletins.length === 0 ? (
          <EmptyState text="No announcements yet — check back soon!" />
        ) : (
          <div className="space-y-3" data-testid="bulletin-list">
            {pinned.map((b) => (
              <Card key={b.id} className="bg-white border-2 border-terracotta/40 p-5 rounded-2xl" data-testid={`bulletin-${b.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Pin className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                  <Badge className="rounded-full bg-terracotta text-white text-[10px] px-2 py-0">PINNED</Badge>
                  <h3 className="font-heading font-semibold">{b.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{fmtDate(b.created_at)} · {b.author}</p>
                <p className="text-sm whitespace-pre-wrap">{b.body}</p>
              </Card>
            ))}
            {recent.map((b) => (
              <Card key={b.id} className="bg-white/90 border border-border p-5 rounded-2xl" data-testid={`bulletin-${b.id}`}>
                <h3 className="font-heading font-semibold mb-1">{b.title}</h3>
                <p className="text-xs text-muted-foreground mb-3">{fmtDate(b.created_at)} · {b.author}</p>
                <p className="text-sm whitespace-pre-wrap">{b.body}</p>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Quick stats at the bottom */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatChip label="Total hours" value={stats.total_hours} />
        <StatChip label="Completed" value={stats.completed_shifts} />
        <StatChip label="Active" value={`${stats.active_shifts} / ${stats.max_active}`} />
        <StatChip label="Dogs walked" value={stats.unique_dogs_walked} />
        <StatChip label="Cats cared for" value={stats.unique_cats_handled || 0} />
      </div>
    </div>
  );
}

function ReportTab({ dogs, cats, reload, issues }) {
  const [species, setSpecies] = useState("dog"); // "dog" | "cat" | "general"
  const [form, setForm] = useState({ dog_id: "", title: "", description: "", severity: "medium" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const animals = species === "cat" ? cats : species === "dog" ? dogs : [];

  function pickSpecies(next) {
    setSpecies(next);
    setForm((f) => ({ ...f, dog_id: "" })); // reset animal when switching
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      let photo_path = null;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = up.data.path;
      }
      await api.post("/issues", { ...form, dog_id: form.dog_id || null, photo_path });
      toast.success("Issue reported. Thank you for speaking up!");
      setForm({ dog_id: "", title: "", description: "", severity: "medium" });
      setSpecies("dog");
      setPhoto(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  }

  return (
    <div className="grid lg:grid-cols-5 gap-6 animate-fade-up">
      <Card className="tactile-card border-0 p-6 lg:col-span-3">
        <h2 className="font-heading text-2xl font-semibold mb-1">Report an issue</h2>
        <p className="text-sm text-muted-foreground mb-5">Tell us about anything we should know — medical concerns, behaviour, facility, etc.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="issue-form">
          <div>
            <Label>What&apos;s this about?</Label>
            <div className="flex gap-2 mt-1.5" role="radiogroup" aria-label="Issue category">
              {[
                { id: "dog", label: "A dog", icon: DogIcon },
                { id: "cat", label: "A cat", icon: Cat },
                { id: "general", label: "General / facility", icon: AlertTriangle },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = species === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => pickSpecies(opt.id)}
                    data-testid={`issue-species-${opt.id}`}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-full h-11 text-sm font-medium border transition-colors ${
                      active
                        ? "bg-fern text-white border-fern"
                        : "bg-white text-ink border-border hover:bg-sand"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {species !== "general" && (
            <div>
              <Label>Which {species}?</Label>
              <Select value={form.dog_id} onValueChange={(v) => setForm({ ...form, dog_id: v })}>
                <SelectTrigger className="rounded-xl h-11" data-testid="issue-dog-select"><SelectValue placeholder={`Select a ${species}`} /></SelectTrigger>
                <SelectContent>
                  {animals.length === 0
                    ? <div className="px-2 py-3 text-sm text-muted-foreground">No {species}s yet.</div>
                    : animals.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="rounded-xl h-11" data-testid="issue-title" placeholder="Short summary" />
          </div>
          <div>
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger className="rounded-xl h-11" data-testid="issue-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High — urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded-xl min-h-[120px]" data-testid="issue-description" />
          </div>
          <div>
            <Label className="flex items-center gap-2"><Camera className="w-4 h-4" strokeWidth={1.5} /> Photo (optional)</Label>
            <Input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)}
              className="rounded-xl" data-testid="issue-photo" />
          </div>
          <Button type="submit" disabled={busy} data-testid="issue-submit"
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
            {busy ? "Submitting…" : "Submit report"}
          </Button>
        </form>
      </Card>

      <div className="lg:col-span-2">
        <h3 className="font-heading text-lg font-semibold mb-3">Your reports</h3>
        {issues.length === 0 ? <EmptyState text="No reports yet." compact /> : (
          <div className="space-y-2" data-testid="my-issues-list">
            {issues.map((i) => (
              <Card key={i.id} className="tactile-card border-0 p-4">
                <div className="flex justify-between items-start gap-2">
                  <p className="font-medium">{i.title}</p>
                  <Badge className={`rounded-full ${issueStatusClass(i.status)} text-white`}>
                    {i.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{i.dog_name || "General"} · {fmtDate(i.created_at)}</p>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogHoursCard({ reloadStats, compact }) {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ hours: "", activity_date: new Date().toISOString().slice(0, 10), description: "" });
  const [busy, setBusy] = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      const { data } = await api.get("/hours/log");
      setEntries(data);
    } catch (e) {
      console.error("Failed to load ad-hoc hours", e);
    }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  async function submit(e) {
    e.preventDefault();
    const h = parseFloat(form.hours);
    if (!(h > 0 && h <= 24)) { toast.error("Hours must be between 0 and 24"); return; }
    if (!form.description.trim()) { toast.error("Please describe what you did"); return; }
    setBusy(true);
    try {
      await api.post("/hours/log", { hours: h, activity_date: form.activity_date, description: form.description.trim() });
      toast.success(`Logged ${h}h — thank you!`);
      setForm({ hours: "", activity_date: new Date().toISOString().slice(0, 10), description: "" });
      await loadEntries();
      if (reloadStats) await reloadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log hours");
    } finally { setBusy(false); }
  }

  async function removeEntry(id) {
    try {
      await api.delete(`/hours/log/${id}`);
      toast.success("Entry removed");
      await loadEntries();
      if (reloadStats) await reloadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  }

  return (
    <Card className="tactile-card border-0 p-6" data-testid="log-hours-card">
      <div className="flex items-center gap-2 mb-1">
        <Plus className="w-5 h-5 text-fern" strokeWidth={1.5} />
        <h3 className="font-heading text-xl font-semibold">Log outside hours</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Did work outside a scheduled shift — laundry, transport, an event? Log it here and it counts toward your total.</p>
      <form onSubmit={submit} className="space-y-3" data-testid="log-hours-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Hours</Label>
            <Input type="number" step="0.25" min="0.25" max="24"
              value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })}
              placeholder="1.5" className="rounded-xl h-11" data-testid="log-hours-input" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" max={new Date().toISOString().slice(0, 10)}
              value={form.activity_date}
              onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
              className="rounded-xl h-11" data-testid="log-hours-date" />
          </div>
        </div>
        <div>
          <Label>What did you do?</Label>
          <Textarea value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Transported animals to vet, event outreach, etc."
            className="rounded-xl min-h-[70px]" maxLength={500}
            data-testid="log-hours-description" />
        </div>
        <Button type="submit" disabled={busy}
          data-testid="log-hours-submit"
          className="rounded-full bg-fern hover:bg-fern-dark text-white">
          {busy ? "Logging…" : "Log hours"}
        </Button>
      </form>

      {!compact && entries.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <p className="label-caps mb-3">Recent entries</p>
          <div className="space-y-2" data-testid="log-hours-list">
            {entries.slice(0, 10).map((e) => (
              <div key={e.id} className="flex justify-between items-start gap-2 p-3 rounded-xl bg-sand/60"
                data-testid={`log-hours-entry-${e.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-full bg-fern text-white">{e.hours}h</Badge>
                    <p className="text-xs text-muted-foreground">{e.activity_date}</p>
                  </div>
                  <p className="text-sm mt-1">{e.description}</p>
                </div>
                <button type="button" onClick={() => removeEntry(e.id)}
                  aria-label="Delete entry"
                  data-testid={`log-hours-delete-${e.id}`}
                  className="text-muted-foreground hover:text-destructive">
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}


function ProfileTab({ user, stats }) {
  return (
    <div className="max-w-2xl animate-fade-up space-y-6">
      <Card className="tactile-card border-0 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-fern text-white flex items-center justify-center text-2xl font-heading font-semibold">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold">{user?.name}</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            {user?.phone && <p className="text-sm text-muted-foreground">{user.phone}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatChip label="Total hours" value={stats.total_hours} />
          <StatChip label="Completed shifts" value={stats.completed_shifts} />
          <StatChip label="Active shifts" value={`${stats.active_shifts}/${stats.max_active}`} />
          <StatChip label="Dogs walked" value={stats.unique_dogs_walked} />
          <StatChip label="Cats cared for" value={stats.unique_cats_handled || 0} />
        </div>
      </Card>
    </div>
  );
}


function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Already installed as PWA / standalone
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Dismissed in this browser
    if (window.localStorage?.getItem("install_banner_dismissed") === "1") return;

    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);

    if (isIOS) setPlatform("ios");
    else if (isAndroid) setPlatform("android");
    else setPlatform("desktop");

    // Only surface on mobile — desktop users don't need this
    if (isIOS || isAndroid) setVisible(true);

    // Chrome/Android: capture the native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    setVisible(false);
    if (typeof window !== "undefined") {
      window.localStorage?.setItem("install_banner_dismissed", "1");
    }
  }

  async function androidInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch (err) { console.debug("Install prompt userChoice rejected", err); }
    setDeferredPrompt(null);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Install Shelter Focus"
      data-testid="install-banner"
      className="mb-4 rounded-2xl bg-gradient-to-br from-fern to-fern-dark text-white p-4 shadow-lg relative"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="install-banner-dismiss"
        className="absolute top-2 right-2 text-white/70 hover:text-white"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 pr-6">
          <p className="font-heading font-semibold leading-tight">Install the volunteer app</p>
          <p className="text-sm text-white/85 mt-1">
            {platform === "ios" && "Tap the Share icon in Safari, then \u201cAdd to Home Screen\u201d."}
            {platform === "android" && (deferredPrompt
              ? "One tap to add Shelter Focus to your home screen."
              : "Tap the browser menu, then \u201cAdd to Home Screen\u201d.")}
            {platform === "desktop" && "Install this app on your phone for one-tap access."}
          </p>
          <div className="flex gap-2 mt-3">
            {platform === "android" && deferredPrompt && (
              <button
                type="button"
                onClick={androidInstall}
                data-testid="install-banner-android-install"
                className="rounded-full bg-white text-fern px-4 py-2 text-sm font-semibold hover:bg-white/90"
              >
                <Download className="w-3.5 h-3.5 inline mr-1" strokeWidth={2} />
                Install now
              </button>
            )}
            <a
              href="/install"
              data-testid="install-banner-help"
              className="rounded-full border border-white/40 px-4 py-2 text-sm font-medium hover:bg-white/10"
            >
              How to install
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text, compact }) {
  return (
    <div className={`text-center ${compact ? "py-8" : "py-12"} tactile-card`}>
      {!compact && <img src={EMPTY_ILLO} alt="" className="w-32 h-32 mx-auto mb-3 opacity-90" />}
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
